// Barang yang Harus Disiapkan / Kebutuhan Barang (Gudang Rido) — ringkasan 4 angka + kartu per pesanan. Baca saja: data /needs-summary yang sudah ada.
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Package, RefreshCw, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROW_ST = { "Cukup": ["Siapkan", "ok"], "Sudah Diambil": ["Sudah Diambil", "ok"], "Kurang": ["Kurang", "warn"], "Kosong": ["Kosong", "bad"],
  "Perlu Dicek": ["Periksa Stok", "check"], "Bukan Stok": ["Bukan Stok", "muted"], "Periksa Manual": ["Manual", "check"], "Ditahan": ["Ditahan", "muted"], "Dibatalkan": ["Dibatalkan", "muted"] };
const simple = (t) => String(t || "").replace(/SKU \S+ /g, "SKU ").replace(/ — perlu pemetaan$/, "");
const fmtTime = (iso) => { try { return new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

export function cardState(c) {
  if (c.block) return [c.block, "muted"];
  if (!(c.lines || []).length) return ["Rincian pesanan belum ada", "check"];   // jangan tampil "Siap" tanpa data
  if ((c.lines || []).some(l => l.manual)) return ["Periksa Barang Manual", "check"];
  if (c.check) return ["Perlu Dicek", "check"];
  return (c.lines || []).some(l => l.status === "Kurang" || l.status === "Kosong") ? ["Stok Kurang", "warn"] : ["Siap", "ok"];   // Siap Diambil hanya bila stok cukup
}

// Baris kebutuhan: hasil AI (bila ada) sebagai bantuan; stok hanya bila varian stok cocok.
export function cardRows(c) {
  const svc = (c.lines || []).filter(l => l.service).map(l => ({ product: l.product || "Jasa", qty: l.qty, stock: null, st: ROW_ST["Bukan Stok"] }));
  const ai = c.ai && (c.ai.rows || []).length && !(c.lines || []).some(l => l.manual) ? c.ai.rows : null;
  if (ai) return { ai: true, rows: [...ai.map(r => ({ product: r.product, color: r.color, sleeve: r.sleeve, size: r.size, qty: r.qty, stock: r.stock,
    st: r.status === "OK" && r.stock != null ? ROW_ST["Cukup"] : ROW_ST["Perlu Dicek"] })), ...svc] };
  return { ai: false, rows: (c.lines || []).filter(l => !l.service).map(l => ({
    product: l.variant_id && l.variant_label ? l.variant_label.split(" · ")[0] : (l.product || "—"), color: l.color, sleeve: l.sleeve, size: l.size,
    qty: l.qty, stock: l.variant_id ? l.available : null, st: ROW_ST[l.status] || ROW_ST["Perlu Dicek"] })).concat(svc) };
}

const lengan = (v) => !v ? "" : /^lengan/i.test(v) ? v : `Lengan ${String(v).toLowerCase()}`;
const fmtDate = (iso) => { try { return iso ? new Date(iso).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" }) : ""; } catch { return ""; } };
const CH = { shopee: "Shopee", tokopedia: "Tokopedia" };
const BADGE = { ok: "Siap Diambil", check: "Perlu Diperiksa", warn: "Stok kurang" };

// Jumlah item/pcs kartu: rincian pesanan; bila kosong → hasil AI tersimpan; bila keduanya tak ada → null (bukan 0).
export function cardCount(c) {
  if ((c.lines || []).length) return { items: c.items, pcs: c.pcs, ai: false };
  const rows = (c.ai && c.ai.rows) || [];
  if (rows.length) return { items: rows.length, pcs: rows.reduce((t, r) => t + (Number(r.qty) || 0), 0), ai: true };
  return null;
}

function noRowsReason(c) {
  if (c.block) return `Pesanan ${String(c.block).toLowerCase()} — tidak perlu disiapkan.`;
  if (c.ai && c.ai.status && !(c.ai.rows || []).length) return `Rincian barang belum tersedia — ${c.ai.status === "Gagal Diperiksa" ? "mockup belum berhasil dibaca" : `hasil baca mockup: ${c.ai.status}`}.`;
  return c.mockup_file_id ? "Rincian barang belum tersedia — data pesanan kosong dan mockup belum dibaca. Periksa mockup manual."
    : "Rincian barang belum tersedia — data pesanan dan mockup belum ada.";
}

function NeedRow({ r, i }) {
  const svc = r.st[0] === "Bukan Stok";
  return <div className="nbx-row" data-testid={`nbx-row-${i}`}>
    <div className="nbx-prod"><b>{r.product || "—"}</b>
      <span className="nbx-var">{[r.color, lengan(r.sleeve)].filter(Boolean).join(" · ") || (r.size ? "" : "Varian belum diketahui")}
        {r.size && <>{r.color || r.sleeve ? " · " : ""}<b className="nbx-size">{r.size}</b></>}</span></div>
    <div className="nbx-qty"><span className="nbx-need">Butuh <b>{r.qty ?? "?"} pcs</b></span>
      <span className="nbx-stk">{svc ? "Jasa · bukan stok" : r.stock != null ? `Stok: ${r.stock} pcs` : "Stok belum diketahui"}</span>
      {!svc && r.st[1] !== "ok" && <span className={`nbx-st is-${r.st[1]}`}>{r.st[0]}</span>}</div>
  </div>;
}

// Buka/tutup memakai state React (bukan <details>/<summary> bawaan) agar klik andal di HP (Safari/Chrome) & desktop.
function NeedsCard({ c, idx }) {
  const [open, setOpen] = useState(idx < 3);
  const [tech, setTech] = useState(false);
  const [thumb, setThumb] = useState(true);
  const [st, tone] = cardState(c);
  const { ai, rows } = cardRows(c);
  const cnt = cardCount(c);
  const manual = st === "Periksa Barang Manual";
  const id = `nbx-body-${c.job_id}`;
  const meta = [fmtDate(c.sent_at), CH[c.channel] || ""].filter(Boolean).join(" · ");
  return <div className={`nbx-card${open ? " is-open" : ""}`} data-testid={`nbx-card-${c.order_number}`}>
    <div className="nbx-head">
      <button type="button" className="nbx-headbtn" aria-expanded={open} aria-controls={id} onClick={() => setOpen(v => !v)}
        data-testid={`nbx-toggle-${c.order_number}`}>
        <span className="nbx-title"><b className="nbx-no">#{c.order_number}</b>
          {c.buyer && <span className="nbx-buyer" data-testid={`nbx-buyer-${c.order_number}`}>{c.buyer}</span>}
          {meta && <span className="nbx-meta">{meta}</span>}
          <span className="nbx-count" data-testid={`nbx-count-${c.order_number}`}>{cnt ? `${cnt.items} item · ${cnt.pcs} pcs${cnt.ai ? " (AI)" : ""}` : "Rincian belum tersedia"}</span></span>
        <span className="nbx-side">
          <span className={`nbx-badge is-${tone}`} data-testid={`nbx-status-${c.order_number}`}>{manual ? "Periksa manual" : BADGE[tone] || st}</span>
          <span className="nbx-more" aria-hidden="true">{open ? "Tutup ▴" : "Lihat ▾"}</span></span>
      </button>
    </div>
    {open && <div id={id} className="nbx-body" data-testid={`nbx-body-${c.order_number}`}>
      {(ai || manual) && <div className="nbx-sub small">{manual ? "Periksa Barang Manual — tanpa AI" : "Hasil Baca Mockup"}</div>}
      {rows.length > 0 && <div className="nbx-rows">
        {c.mockup_file_id && thumb && <a className="nbx-thumb" href={`${API}/shopee/files/${encodeURIComponent(c.mockup_file_id)}/preview`} target="_blank" rel="noopener noreferrer"
          aria-label="Lihat mockup asli" data-testid={`nbx-mockup-${c.order_number}`}>
          <img src={`${API}/shopee/files/${encodeURIComponent(c.mockup_file_id)}/thumbnail?size=120`} alt="Mockup" loading="lazy" onError={() => setThumb(false)}/></a>}
        <div className="nbx-rowlist">{rows.map((r, i) => <NeedRow key={i} r={r} i={i}/>)}</div>
      </div>}
      {!rows.length && <p className="nbx-none" data-testid={`nbx-norows-${c.order_number}`}>{noRowsReason(c)}
        {c.mockup_file_id && <> <a href={`${API}/shopee/files/${encodeURIComponent(c.mockup_file_id)}/preview`} target="_blank" rel="noopener noreferrer"
          data-testid={`nbx-mockup-${c.order_number}`}>Lihat mockup <ExternalLink size={12}/></a></>}</p>}
      {c.check > 0 && <div className="nbx-why" data-testid={`nbx-why-${c.order_number}`}>
        <b>Perlu Diperiksa:</b>
        <ul>{c.reasons.slice(0, 2).map((t, i) => <li key={i}>{simple(t)}</li>)}</ul>
        <button type="button" className="link-btn small" aria-expanded={tech} onClick={() => setTech(v => !v)} data-testid={`nbx-tech-toggle-${c.order_number}`}>
          {tech ? "Tutup detail" : `Lihat semua detail (${c.lines.length})`}</button>
        {tech && <ul className="nbx-tech" data-testid={`nbx-tech-${c.order_number}`}>{c.lines.map((l, i) => <li key={i}>{l.product || "—"} {l.sku ? `· SKU ${l.sku}` : ""} · {l.color || "—"} · {l.size || "—"} · {l.qty} pcs — {l.status}{(l.checks || []).length ? `: ${l.checks.join(" · ")}` : ""}</li>)}</ul>}
      </div>}
    </div>}
  </div>;
}

// Ringkasan pcs: angka server (rincian pesanan) + hasil AI tersimpan utk pesanan tanpa rincian. Tanpa data → tidak dihitung sebagai 0.
export function pcsSummary(s) {
  const cards = s.cards || [];
  let aiPcs = 0, known = 0, unknown = 0;
  for (const c of cards) {
    if (c.block) continue;
    const n = cardCount(c);
    if (!n) unknown++; else { known++; if (n.ai) aiPcs += n.pcs; }
  }
  if (cards.length < (s.jobs || 0)) unknown = Math.max(unknown, s.nodata_orders || 0);
  return { need: (s.need_pcs || 0) + aiPcs, sure: Math.max(0, (s.need_pcs || 0) - (s.unknown_pcs || 0)), known, unknown };
}

export function WhNeedsSummaryBar({ compact = false }) {
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("new");
  const [err, setErr] = useState("");
  const [jump, setJump] = useState(0);
  const listRef = useRef(null);
  useEffect(() => { if (jump) listRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" }); }, [jump]);
  const load = () => { setErr(""); return axios.get(`${API}/shopee/warehouse/needs-summary`, { withCredentials: true, timeout: 30000 })
    .then(r => setS(r.data)).catch(e => setErr(e?.response?.data?.detail || "Gagal memuat Barang yang Harus Disiapkan")); };
  useEffect(() => { load(); }, []);
  if (!s) return <div className="nbx nbx-top small" data-testid="wh-needs-board">
    <Package size={16}/> <b>{compact ? "Lihat per pesanan" : "Barang yang Harus Disiapkan"}</b>
    {err ? <><span className="nbx-err" data-testid="nbx-error">{String(err)}</span>
      <button type="button" className="link-btn small" onClick={load} data-testid="nbx-retry">Coba lagi</button></>
      : <span className="muted" data-testid="nbx-loading">Memuat…</span>}
  </div>;
  if (!s.jobs) return <div className="nbx nbx-top small" data-testid="wh-needs-board">
    <Package size={16}/> <b>{compact ? "Lihat per pesanan" : "Barang yang Harus Disiapkan"}</b> <span className="muted" data-testid="nbx-empty">Belum ada pesanan di antrean Gudang.</span>
    <button type="button" className="link-btn" onClick={load} aria-label="Muat ulang" data-testid="nbx-refresh"><RefreshCw size={14}/></button>
  </div>;
  const cards = s.cards || [];
  const noData = (c) => !(c.lines || []).length && !c.block;
  const ready = cards.filter(c => cardState(c)[0] === "Siap"), check = cards.filter(c => c.check > 0 || noData(c));
  const full = cards.length >= (s.jobs || 0);
  const ps = pcsSummary(s);
  const nod = ps.unknown;
  const pcs = (v) => (ps.known ? v : "Belum tersedia");
  const shown = (filter === "ready" ? ready : filter === "check" ? check : cards).slice()
    .sort((a, b) => sort === "num" ? String(a.order_number).localeCompare(String(b.order_number)) : String(b.sent_at || "").localeCompare(String(a.sent_at || "")));
  const tiles = [["Pesanan", s.jobs, "blue", "all"], ["Total Pcs", pcs(ps.need), "green", "all"], ["Pcs Teridentifikasi", pcs(ps.sure), "red", "all"],
    ["Perlu Diperiksa", full ? check.length : (s.check_orders ?? 0) + nod, "amber", "check"]];
  const show = (k) => { setFilter(k); setOpen(true); setJump(n => n + 1); };
  const chip = (k, label, nn) => <button type="button" className={`nbx-chip${filter === k ? " is-on" : ""}`} onClick={() => { setFilter(k); setOpen(true); }}
    data-testid={`nbx-filter-${k}`}>{label} ({nn})</button>;
  return <div className={compact ? "nbx nbx-compact" : "nbx"} data-testid="wh-needs-board">
    <div className="nbx-top">
      <button type="button" className="nbx-toggle" aria-expanded={open} onClick={() => { if (!open) setJump(n => n + 1); setOpen(v => !v); }} data-testid="wh-needs-summary">
        <Package size={16}/> <b>{compact ? "Lihat per pesanan" : "Barang yang Harus Disiapkan"}</b> <span className="nbx-more">{open ? "Tutup ▴" : "Lihat ▾"}</span></button>
      <span className="muted small">{s.updated_at ? `Update: ${fmtTime(s.updated_at)}` : ""}</span>
      <button type="button" className="link-btn" onClick={load} aria-label="Muat ulang" data-testid="nbx-refresh"><RefreshCw size={14}/></button>
    </div>
    {!compact && <div className="nbx-tiles">{tiles.map(([l, v, c, k]) => <div key={l} className={`nbx-tile is-${c}${typeof v === "string" ? " is-na" : ""}`} data-testid={`nbx-tile-${c}`}
      role="button" tabIndex={0} aria-label={`${l}: lihat daftar`} onClick={() => show(k)} onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); show(k); } }}><b>{v}</b><span>{l}</span></div>)}</div>}
    {!compact && nod > 0 && <p className="small muted nbx-nodata" data-testid="nbx-nodata">{nod} pesanan belum ada rincian barang{ps.known ? " — belum termasuk Total Pcs" : ""}.</p>}
    {!full && <p className="small muted nbx-nodata" data-testid="nbx-partial">Daftar menampilkan {cards.length} dari {s.jobs} pesanan. Angka ringkasan dihitung dari semua {s.jobs} pesanan.</p>}
    {open && <>
      <div className="nbx-bar" ref={listRef}>
        {chip("all", "Semua", cards.length)}{chip("ready", "Siap Diambil", ready.length)}{chip("check", "Perlu Diperiksa", check.length)}
        <select value={sort} onChange={e => setSort(e.target.value)} aria-label="Urutkan" data-testid="nbx-sort">
          <option value="new">Urut: Terbaru</option><option value="num">Urut: Nomor Pesanan</option></select>
      </div>
      <div data-testid="wh-needs-summary-detail">
        {shown.map((c, i) => <NeedsCard key={c.job_id} c={c} idx={i}/>)}
        {!shown.length && <p className="muted small">Tidak ada pesanan di filter ini.</p>}
      </div>
    </>}
  </div>;
}
